/**
 * SearchByCP.jsx — Prospection par code postal
 * Deux onglets :
 *   1. "Biens scorés" — biens DVF avec probabilité de revente (P6)
 *   2. "Biens invisibles" — parcelles jamais vendues depuis 2014
 *   3. "Maisons silencieuses" — classement v2 (probabilité de vente dans les 12 mois, calibrée ; décile DPE secondaire)
 *   4. "Prix du secteur" — prix de vente réels DVF par commune et tranche, estimation par comparables
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Chip, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Grid, Paper,
  Slider, Alert, Tooltip, Divider, Collapse, Tabs, Tab, Switch, FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import ScoreExplanationPanel from './ScoreExplanationPanel';
import SuiviProspection, { SuiviChip, directionsUrl } from './SuiviProspection';

// ─── Config ────────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || '';

const PRIORITY_CONFIG = {
  URGENT: { color: '#c62828', bg: '#ffebee', label: '🔴 URGENT' },
  HIGH:   { color: '#e65100', bg: '#fff3e0', label: '🟠 HIGH' },
  MEDIUM: { color: '#f9a825', bg: '#fffde7', label: '🟡 MEDIUM' },
  LOW:    { color: '#2e7d32', bg: '#e8f5e9', label: '🟢 LOW' },
  NONE:   { color: '#757575', bg: '#f5f5f5', label: '⚪ NONE' },
};

// ─── Hook Leaflet ──────────────────────────────────────────────────────────────
function useLeaflet() {
  // Chargement idempotent : un seul script Leaflet même si l'effet est exécuté deux fois (mode développement de React) ;
  // sinon deux copies de la bibliothèque coexistent et les marqueurs créés avec l'une ne s'affichent pas sur une carte créée avec l'autre.
  const [L, setL] = useState(window.L || null);
  useEffect(() => {
    if (window.L) { setL(window.L); return undefined; }
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link'); link.id = 'leaflet-css'; link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(link);
    }
    let script = document.getElementById('leaflet-js');
    if (!script) {
      script = document.createElement('script'); script.id = 'leaflet-js'; script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      document.head.appendChild(script);
    }
    const onload = () => setL(window.L);
    script.addEventListener('load', onload);
    return () => script.removeEventListener('load', onload);
  }, []);
  return L;
}

// ─── Composant Carte ──────────────────────────────────────────────────────────
function ProspectMap({ biens, L, onSelectBien, selectedId }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const markersByIdRef = useRef({});

  useEffect(() => {
    if (!L || !mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, { zoomControl: true }).setView([46.8, 2.3], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>', maxZoom: 19,
    }).addTo(map);
    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [L]);

  useEffect(() => {
    if (!L || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];
    markersByIdRef.current = {};
    const withCoords = (biens || []).filter(b => b.latitude && b.longitude);
    if (!withCoords.length) return;
    map.fitBounds(L.latLngBounds(withCoords.map(b => [b.latitude, b.longitude])), { padding: [30, 30], maxZoom: 14 });
    withCoords.forEach(bien => {
      const cfg = PRIORITY_CONFIG[bien.contact_priority] || PRIORITY_CONFIG.NONE;
      const pct = Math.round((bien.prob_sell_6m || 0) * 100);
      const icon = L.divIcon({
        className: '',
        html: `<div style="width:16px;height:16px;border-radius:50%;background:${cfg.color};border:2.5px solid white;box-shadow:0 1px 5px rgba(0,0,0,0.45);cursor:pointer;" title="${pct}%"></div>`,
        iconSize: [16, 16], iconAnchor: [8, 8],
      });
      const marker = L.marker([bien.latitude, bien.longitude], { icon }).addTo(map)
        .bindPopup(`<div style="font-family:sans-serif;min-width:180px">
          <b style="font-size:0.95rem">${bien.adresse || '—'}</b><br/>
          <span style="color:#555">${bien.commune || ''}</span><br/>
          <hr style="margin:4px 0"/>
          <b>${bien.score_label || 'P6'} : </b><span style="color:${cfg.color};font-weight:700">${bien.score_chance ? bien.score_chance + ' (' + ((bien.prob_sell_6m || 0) * 100).toFixed(1) + ' %)' : pct + '%'}</span>
          &nbsp;<span style="font-size:0.8rem">${cfg.label}</span><br/>
          <b>Type : </b>${bien.type_local || '—'}<br/>
          <b>Surface : </b>${bien.surface ? bien.surface + ' m²' : '—'}<br/>
          <b>Dernière vente : </b>${bien.date_mutation || '—'}
        </div>`);
      marker.on('click', () => onSelectBien && onSelectBien(bien));
      markersRef.current.push(marker);
      markersByIdRef.current[bien.id] = marker;
    });
  }, [biens, L, onSelectBien]);

  // Bien sélectionné dans le tableau : centrage sur la carte et ouverture de sa fiche
  useEffect(() => {
    if (!L || !mapInstanceRef.current || selectedId == null) return;
    const marker = markersByIdRef.current[selectedId];
    if (!marker) return;
    const map = mapInstanceRef.current;
    map.setView(marker.getLatLng(), Math.max(map.getZoom(), 16), { animate: true });
    marker.openPopup();
  }, [selectedId, L]);

  if (!L) return (
    <Box sx={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
      <CircularProgress size={28} /><Typography sx={{ ml: 2, color: '#888' }}>Chargement…</Typography>
    </Box>
  );
  return <div ref={mapRef} style={{ height: 380, width: '100%', borderRadius: 8 }} />;
}

// ─── Colonnes biens scorés ─────────────────────────────────────────────────────
const buildColumns = () => [
  {
    field: 'prob_sell_6m', headerName: 'P6', width: 80, sortable: true,
    renderCell: (p) => {
      const pct = Math.round((p.value || 0) * 100);
      const cfg = PRIORITY_CONFIG[p.row.contact_priority] || PRIORITY_CONFIG.NONE;
      return <Box sx={{ fontWeight: 800, color: cfg.color, fontSize: '1.05rem' }}>{pct}%</Box>;
    },
  },
  {
    field: 'contact_priority', headerName: 'Priorité', width: 115,
    renderCell: (p) => {
      const cfg = PRIORITY_CONFIG[p.value] || PRIORITY_CONFIG.NONE;
      return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '0.68rem', height: 22 }} />;
    },
  },
  { field: 'adresse', headerName: 'Adresse', flex: 1, minWidth: 180 },
  { field: 'type_local', headerName: 'Type', width: 115 },
  { field: 'surface', headerName: 'Surface', width: 90, renderCell: (p) => p.value ? `${p.value} m²` : '—' },
  { field: 'prix', headerName: 'Dernier prix', width: 120, renderCell: (p) => p.value ? `${(p.value / 1000).toFixed(0)}k€` : '—' },
  { field: 'prix_m2', headerName: '€/m²', width: 85, renderCell: (p) => p.value ? `${Number(p.value).toFixed(0)}€` : '—' },
  { field: 'date_mutation', headerName: 'Dernière vente', width: 130 },
  { field: 'duree_detention', headerName: 'Détention', width: 100, renderCell: (p) => p.value != null ? `${p.value} ans` : '—' },
  { field: 'classe_dpe', headerName: 'DPE', width: 65 },
  { field: 'suivi', headerName: 'Suivi', width: 170, sortable: false, renderCell: (p) => <SuiviChip suivi={p.value} /> },
  {
    field: 'propensity_timeframe', headerName: 'Horizon', width: 210,
    renderCell: (p) => <span style={{ fontSize: '0.78rem', color: '#555' }}>{p.value || '—'}</span>,
  },
];

// ─── Colonnes biens invisibles ─────────────────────────────────────────────────
const buildInvisiblesColumns = () => [
  {
    field: 'ref_cadastrale', headerName: 'Référence cadastrale', width: 180,
    renderCell: (p) => (
      <Tooltip title="Cliquez sur la ligne pour voir sur le cadastre">
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: '#1565c0' }}>
          {p.value || '—'}
        </Box>
      </Tooltip>
    ),
  },
  { field: 'commune', headerName: 'Commune', flex: 1, minWidth: 140 },
  {
    field: 'surface_m2', headerName: 'Surface (m²)', width: 130, sortable: true,
    renderCell: (p) => {
      if (!p.value) return '—';
      const surface = p.value;
      let color = '#555';
      if (surface >= 500) color = '#1b5e20';
      else if (surface >= 100) color = '#2e7d32';
      else if (surface >= 80) color = '#388e3c';
      return <Box sx={{ fontWeight: 600, color }}>{Number(surface).toLocaleString('fr-FR')} m²</Box>;
    },
  },
  {
    field: 'id_parcelle', headerName: 'ID Parcelle', width: 150,
    renderCell: (p) => <Box sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#777' }}>{p.value || '—'}</Box>,
  },
  {
    field: 'latitude', headerName: 'Coordonnées', width: 140,
    renderCell: (p) => p.value ? (
      <Tooltip title="Voir sur Google Maps / itinéraire">
        <Chip
          label="📍 Géolocalisé"
          size="small"
          clickable
          component="a"
          href={`https://www.google.com/maps/dir/?api=1&destination=${p.row.latitude},${p.row.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.7rem', height: 20, cursor: 'pointer', '&:hover': { bgcolor: '#c8e6c9' } }}
        />
      </Tooltip>
    ) : (
      <Chip label="Non géolocalisé" size="small" sx={{ bgcolor: '#f5f5f5', color: '#aaa', fontSize: '0.7rem', height: 20 }} />
    ),
  },
  {
    field: 'actions_cadastre', headerName: 'Cadastre', width: 120, sortable: false,
    renderCell: (p) => {
      const { code_dept, code_commune, section, numero } = p.row;
      if (!section || !numero) return null;
      const dept = code_dept || '';
      const comm = code_commune ? code_commune.substring(2) : '';
      const sec = (section || '').trim().toUpperCase();
      const num = (numero || '').trim().padStart(4, '0');
      const url = `https://cadastre.gouv.fr/scpc/rechercherPlan.do?codeDepartement=${dept}&codeCommune=${comm}&section=${sec}&numeroParcelle=${num}`;
      return (
        <Button
          size="small"
          variant="text"
          startIcon={<OpenInNewIcon sx={{ fontSize: '0.85rem !important' }} />}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          sx={{ fontSize: '0.72rem', textTransform: 'none', minWidth: 0, px: 1 }}
        >
          Voir
        </Button>
      );
    },
  },
];

// ─── Colonnes maisons silencieuses (v2) ───────────────────────────────────────
// Probabilité → « 1 sur N » (une maison sur N de ce profil se vend dans l'année)
export const formatChance = (p) => (p == null || p <= 0) ? '—' : `1 sur ${Math.round(1 / p)}`;
const DECILE_COLOR = (d) => d === 1 ? '#c62828' : d === 2 ? '#e65100' : d === 3 ? '#f9a825' : '#757575';
const buildV2Columns = () => [
  {
    field: 'decile', headerName: 'Décile', width: 90,
    renderCell: (p) => p.value ? <Chip label={`D${p.value}`} size="small" sx={{ bgcolor: DECILE_COLOR(p.value), color: 'white', fontWeight: 700, height: 22 }} /> : '—',
  },
  { field: 'score', headerName: 'Chance de vente (12 mois)', width: 175, renderCell: (p) => p.value != null ? <Box><b>{formatChance(p.value)}</b> <span style={{ color: '#888', fontSize: '0.75rem' }}>({(p.value * 100).toFixed(1)} %)</span></Box> : '—' },
  { field: 'decile_dpe', headerName: 'Décile DPE', width: 95, renderCell: (p) => p.value ? `D${p.value}` : '—' },
  { field: 'adresse', headerName: 'Adresse', flex: 1, minWidth: 200, renderCell: (p) => p.row.adresse_completee ? <Tooltip title="Bâtiment sans adresse dans la base nationale : adresse la plus proche d'après ses coordonnées"><span style={{ fontStyle: 'italic' }}>{p.value} ≈</span></Tooltip> : p.value },
  { field: 'commune', headerName: 'Commune', width: 150 },
  { field: 'raisons', headerName: 'Pourquoi', flex: 1.2, minWidth: 240, renderCell: (p) => <span style={{ fontSize: '0.78rem', color: '#555' }}>{p.value || '—'}</span> },
  { field: 'signaux_internes', headerName: 'Déjà connu', width: 130, renderCell: (p) => p.value ? <Tooltip title={`${p.value}${p.row.stop_call ? ' · opposition au démarchage téléphonique' : ''}${p.row.stop_mail ? ' · opposition au courrier' : ''}`}><Chip label={p.row.stop_call || p.row.stop_mail ? '🚫 Netty' : 'Netty'} size="small" sx={{ bgcolor: '#eceff1', color: '#37474f', fontWeight: 700, height: 22 }} /></Tooltip> : '' },
  { field: 'annee_construction', headerName: 'Constr.', width: 80 },
  { field: 'emprise', headerName: 'Au sol', width: 80, renderCell: (p) => p.value ? `${p.value} m²` : '—' },
  { field: 'surf_parcelle', headerName: 'Parcelle', width: 90, renderCell: (p) => p.value ? `${Number(p.value).toLocaleString('fr-FR')} m²` : '—' },
  { field: 'derniere_vente', headerName: 'Dernière vente', width: 120, renderCell: (p) => p.value || 'jamais (2014+)' },
  { field: 'dernier_prix', headerName: 'Prix', width: 90, renderCell: (p) => p.value ? `${(p.value / 1000).toFixed(0)}k€` : '—' },
  { field: 'dpe_classe', headerName: 'DPE', width: 65, renderCell: (p) => p.value ? `${p.value} (${(p.row.dpe_date || '').slice(0, 4)})` : '—' },
  { field: 'suivi', headerName: 'Suivi', width: 170, sortable: false, renderCell: (p) => <SuiviChip suivi={p.value} /> },
  { field: 'nb_demandes_estimation', headerName: '📩 Estim.', width: 95, renderCell: (p) => p.value ? <Chip label={`${p.value} · ${(p.row.derniere_demande_estimation || '').slice(2, 10)}`} size="small" sx={{ bgcolor: '#e3f2fd', color: '#0d47a1', fontWeight: 700, height: 20, fontSize: '0.68rem' }} /> : '' },
  {
    field: 'actions', headerName: 'Liens', width: 150, sortable: false,
    renderCell: (p) => {
      const { code_dept, code_commune, section, numero, latitude, longitude } = p.row;
      const cad = section && numero ? `https://cadastre.gouv.fr/scpc/rechercherPlan.do?codeDepartement=${code_dept}&codeCommune=${(code_commune || '').substring(2)}&section=${section}&numeroParcelle=${numero}` : null;
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {cad && <Button size="small" variant="text" href={cad} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} sx={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0, px: 0.8 }}>Cadastre</Button>}
          {latitude && <Button size="small" variant="text" href={`https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} sx={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0, px: 0.8 }}>Itinéraire</Button>}
        </Box>
      );
    },
  },
];

// ─── Colonnes prix par commune ────────────────────────────────────────────────
const fmtEur = (v) => v == null ? '—' : `${Number(v).toLocaleString('fr-FR')} €`;
const buildPrixColumns = () => [
  { field: 'commune', headerName: 'Commune', flex: 1, minWidth: 160 },
  { field: 'ventes_12m', headerName: 'Ventes 12 mois', width: 120 },
  { field: 'prix_m2_12m', headerName: '€/m² 12 mois', width: 115, renderCell: (p) => <b>{fmtEur(p.value)}</b> },
  { field: 'fourchette', headerName: 'Fourchette (q1 à q3)', width: 170, valueGetter: (p) => p.row.prix_m2_12m_q1 ? `${fmtEur(p.row.prix_m2_12m_q1)} à ${fmtEur(p.row.prix_m2_12m_q3)}` : '—' },
  { field: 'evolution_12m_pct', headerName: 'Évolution', width: 100, renderCell: (p) => p.value == null ? '—' : <span style={{ color: p.value >= 0 ? '#2e7d32' : '#c62828', fontWeight: 700 }}>{p.value > 0 ? '+' : ''}{p.value} %</span> },
  { field: 'prix_median_12m', headerName: 'Prix médian', width: 120, renderCell: (p) => fmtEur(p.value) },
  { field: 'surface_mediane_12m', headerName: 'Surface méd.', width: 110, renderCell: (p) => p.value ? `${p.value} m²` : '—' },
  { field: 'prix_m2_5ans', headerName: '€/m² 5 ans', width: 105, renderCell: (p) => fmtEur(p.value) },
  { field: 'ventes_5ans', headerName: 'Ventes 5 ans', width: 110 },
];

// ─── Composant principal ───────────────────────────────────────────────────────
export default function SearchByCP() {
  // Filtres
  const [cp, setCp] = useState(() => { try { return new URLSearchParams(window.location.search).get('cp') || ''; } catch (e) { return ''; } });
  const [typeLocal, setTypeLocal] = useState('');
  const [priorite, setPriorite] = useState('');
  const [surfaceMin, setSurfaceMin] = useState('');
  const [surfaceMax, setSurfaceMax] = useState('');
  const [minP6, setMinP6] = useState(0);
  const [anneeMin, setAnneeMin] = useState('');
  const [anneeMax, setAnneeMax] = useState('');
  const [sortDvf, setSortDvf] = useState('score');
  const [surfaceMinInv, setSurfaceMinInv] = useState('');

  // État
  const [loading, setLoading] = useState(false);
  const [loadingInv, setLoadingInv] = useState(false);
  const [data, setData] = useState(null);
  const [dataInv, setDataInv] = useState(null);
  const [error, setError] = useState('');
  const [errorInv, setErrorInv] = useState('');
  const [selectedBien, setSelectedBien] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // v2 : maisons silencieuses
  const [dataV2, setDataV2] = useState(null);
  const [loadingV2, setLoadingV2] = useState(false);
  const [errorV2, setErrorV2] = useState('');
  const [decileMax, setDecileMax] = useState(3);
  const [onlySilent, setOnlySilent] = useState(true);
  // prix (v2)
  const [dataPrix, setDataPrix] = useState(null);
  const [loadingPrix, setLoadingPrix] = useState(false);
  const [errorPrix, setErrorPrix] = useState('');
  const [typePrix, setTypePrix] = useState('Maison');
  const [estimBien, setEstimBien] = useState(null);
  const [suiviDvf, setSuiviDvf] = useState({});
  const [suiviV2, setSuiviV2] = useState({});
  const [estimSurface, setEstimSurface] = useState('');
  const [estim, setEstim] = useState(null);
  const [loadingEstim, setLoadingEstim] = useState(false);

  const L = useLeaflet();
  const columns = buildColumns();
  const columnsInv = buildInvisiblesColumns();
  const columnsV2 = buildV2Columns();
  const columnsPrix = buildPrixColumns();

  // ── Suivi de prospection (statuts des biens listés) ───────────────────────
  const loadSuivi = async (source, ids, setter) => {
    if (!ids.length) { setter({}); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/v2/suivi?source=${source}&cles=${encodeURIComponent(ids.join(','))}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 15000,
      });
      setter(res.data || {});
    } catch (e) { setter({}); }
  };
  const openDirections = (row) => { const u = directionsUrl(row); if (u) window.open(u, '_blank', 'noopener'); };
  const selectV2 = (row) => { setEstimBien(row); setEstimSurface(row.derniere_surface || ''); setEstim(null); };

  // ── Recherche biens scorés ──────────────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    const cpTrimmed = cp.trim();
    if (!cpTrimmed) { setError('Veuillez saisir un code postal.'); return; }
    setLoading(true); setError(''); setData(null); setSelectedBien(null);
    try {
      const params = new URLSearchParams({ limit: 300, min_p6: minP6, sort: sortDvf });
      if (anneeMin) params.append('annee_min', anneeMin);
      if (anneeMax) params.append('annee_max', anneeMax);
      if (typeLocal) params.append('type_local', typeLocal);
      if (priorite) params.append('priorite', priorite);
      if (surfaceMin) params.append('surface_min', surfaceMin);
      if (surfaceMax) params.append('surface_max', surfaceMax);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/analyze/${cpTrimmed}?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 10000,
      });
      setData(res.data);
      loadSuivi('dvf', (res.data.biens || []).map(b => b.id), setSuiviDvf);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Erreur lors de la recherche.');
    } finally { setLoading(false); }
  }, [cp, typeLocal, priorite, surfaceMin, surfaceMax, minP6, anneeMin, anneeMax, sortDvf]);

  // ── Recherche biens invisibles ─────────────────────────────────────────────
  const handleSearchInvisibles = useCallback(async () => {
    const cpTrimmed = cp.trim();
    if (!cpTrimmed) { setErrorInv('Veuillez saisir un code postal.'); return; }
    setLoadingInv(true); setErrorInv(''); setDataInv(null);
    try {
      const params = new URLSearchParams({ limit: 1000 });
      if (surfaceMinInv) params.append('surface_min', surfaceMinInv);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/biens-invisibles?cp=${cpTrimmed}&${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 15000,
      });
      setDataInv(res.data);
    } catch (e) {
      setErrorInv(e.response?.data?.detail || e.message || 'Erreur lors de la recherche.');
    } finally { setLoadingInv(false); }
  }, [cp, surfaceMinInv]);

  // ── Échantillon témoin : n maisons tirées au sort hors déciles 1-3, inscrites « à prospecter » (groupe témoin) ──
  const handleTemoins = async () => {
    const cpTrimmed = cp.trim();
    if (!cpTrimmed) { setErrorV2('Veuillez saisir un code postal.'); return; }
    const n = window.prompt(`Combien de maisons témoins tirer au sort sur le ${cpTrimmed} ?\n(hors déciles 1 à 3, à prospecter comme les autres ; conseil : 5 à 10 % des portes prévues)`, '10');
    if (!n) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/v2/suivi/temoins?cp=${cpTrimmed}&n=${parseInt(n, 10) || 10}`, null, { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 20000 });
      window.alert(`${res.data.ajoutes} maison(s) témoin ajoutée(s) au suivi de prospection du ${cpTrimmed}. Elles apparaissent dans la liste avec la pastille 🎲.`);
      handleSearchV2();
    } catch (e) { setErrorV2(e.response?.data?.detail || e.message); }
  };

  // ── Recherche maisons silencieuses (v2) ───────────────────────────────────
  const handleSearchV2 = useCallback(async () => {
    const cpTrimmed = cp.trim();
    if (!cpTrimmed) { setErrorV2('Veuillez saisir un code postal.'); return; }
    setLoadingV2(true); setErrorV2(''); setDataV2(null);
    try {
      const params = new URLSearchParams({ cp: cpTrimmed, decile_max: decileMax, silencieuses: onlySilent, limit: 1500 });
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/v2/maisons?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 20000,
      });
      setDataV2(res.data);
      loadSuivi('v2', (res.data.biens || []).map(b => b.id), setSuiviV2);
    } catch (e) {
      setErrorV2(e.response?.data?.detail || e.message || 'Erreur lors de la recherche.');
    } finally { setLoadingV2(false); }
  }, [cp, decileMax, onlySilent]);

  // ── Prix du secteur (v2) ───────────────────────────────────────────────────
  const handleSearchPrix = useCallback(async () => {
    const cpTrimmed = cp.trim();
    if (!cpTrimmed) { setErrorPrix('Veuillez saisir un code postal.'); return; }
    setLoadingPrix(true); setErrorPrix(''); setDataPrix(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/v2/prix?cp=${cpTrimmed}&type_local=${encodeURIComponent(typePrix)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 30000,
      });
      setDataPrix(res.data);
    } catch (e) {
      setErrorPrix(e.response?.data?.detail || e.message || 'Erreur lors du calcul des prix.');
    } finally { setLoadingPrix(false); }
  }, [cp, typePrix]);

  const handleEstimer = async (bien, surface) => {
    const s = Number(surface);
    if (!bien || !s || s < 15) { setEstim({ error: 'Indiquez une surface habitable (m²).' }); return; }
    setLoadingEstim(true); setEstim(null);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ type_local: 'Maison', surface: s, commune: bien.commune || '' });
      if (bien.latitude && bien.longitude) { params.append('lat', bien.latitude); params.append('lon', bien.longitude); }
      const res = await axios.get(`${API_BASE}/v2/estimation?${params}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 30000 });
      setEstim(res.data);
    } catch (e) {
      setEstim({ error: e.response?.data?.detail || e.message });
    } finally { setLoadingEstim(false); }
  };

  // Lancer la recherche invisibles quand on bascule sur cet onglet
  useEffect(() => {
    if (activeTab === 1 && cp.trim() && !dataInv && !loadingInv) {
      handleSearchInvisibles();
    }
    if (activeTab === 2 && cp.trim() && !dataV2 && !loadingV2) {
      handleSearchV2();
    }
    if (activeTab === 3 && cp.trim() && !dataPrix && !loadingPrix) {
      handleSearchPrix();
    }
  }, [activeTab]); // eslint-disable-line

  // ── Recalcul modèle ────────────────────────────────────────────────────────
  const handleRecompute = async () => {
    if (!window.confirm('Lancer le recalcul P6 sur toute la base ?\n(Peut prendre plusieurs minutes)')) return;
    setRecomputing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/recompute-model?limit=5000`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 300000,
      });
      alert(`✅ ${res.data.message}`);
      if (data) handleSearch();
    } catch (e) {
      alert(`❌ Erreur : ${e.response?.data?.detail || e.message}`);
    } finally { setRecomputing(false); }
  };

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (!data?.biens?.length) return;
    const headers = ['Adresse','Commune','CP','Type','Surface (m²)','Prix (€)','€/m²','Date vente','Détention (ans)','P6 (%)','Priorité','Horizon','DPE','Latitude','Longitude'];
    const rows = data.biens.map(b => [b.adresse,b.commune,b.code_postal,b.type_local,b.surface,b.prix,b.prix_m2,b.date_mutation,b.duree_detention,Math.round((b.prob_sell_6m||0)*100),b.contact_priority,b.propensity_timeframe,b.classe_dpe,b.latitude,b.longitude]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${v??''}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`prospects_${cp}_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportInvCSV = () => {
    if (!dataInv?.biens?.length) return;
    const headers = ['Référence cadastrale','Commune','CP','Surface (m²)','ID Parcelle','Latitude','Longitude'];
    const rows = dataInv.biens.map(b => [b.ref_cadastrale,b.commune,b.code_postal,b.surface_m2,b.id_parcelle,b.latitude,b.longitude]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${v??''}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`invisibles_${cp}_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportV2CSV = () => {
    if (!dataV2?.biens?.length) return;
    const headers = ['Décile','Chance de vente (1 sur N)','P(vente 12 m) %','Décile DPE','Déjà connu (Netty)','Adresse','Commune','CP','Pourquoi','Construction','Au sol (m²)','Parcelle (m²)','Dernière vente','Prix (€)','DPE','Date DPE','Référence parcelle','Latitude','Longitude'];
    const rows = dataV2.biens.map(b => [b.decile,formatChance(b.score),((b.score||0)*100).toFixed(1),b.decile_dpe,b.signaux_internes||'',b.adresse,b.commune,b.code_postal,b.raisons,b.annee_construction,b.emprise,b.surf_parcelle,b.derniere_vente,b.dernier_prix,b.dpe_classe,b.dpe_date,b.parcelle,b.latitude,b.longitude]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${v??''}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`silencieuses_${cp}_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>

      {/* En-tête */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight={800} gutterBottom>🎯 Prospection par code postal</Typography>
          <Typography variant="body2" color="text.secondary">
            Biens à vendre dans les 6 mois · Parcelles jamais vendues depuis 2014 · Maisons silencieuses classées (v2) · Prix réels du secteur
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title="Recalculer le score P6 sur toute la base">
            <Button variant="outlined" size="small" color="warning"
              startIcon={recomputing ? <CircularProgress size={14}/> : <RefreshIcon/>}
              onClick={handleRecompute} disabled={recomputing}>
              {recomputing ? 'Calcul en cours…' : 'Recalculer P6'}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Barre de recherche */}
      <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3} md={2}>
            <TextField fullWidth label="Code postal" value={cp}
              onChange={e => setCp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (activeTab === 0 ? handleSearch() : activeTab === 1 ? handleSearchInvisibles() : activeTab === 2 ? handleSearchV2() : handleSearchPrix())}
              size="small" placeholder="76260" inputProps={{ maxLength: 5 }} />
          </Grid>
          <Grid item xs={12} sm={2} md={1.5}>
            <Button fullWidth variant="contained" size="medium"
              onClick={activeTab === 0 ? handleSearch : activeTab === 1 ? handleSearchInvisibles : activeTab === 2 ? handleSearchV2 : handleSearchPrix}
              disabled={loading || loadingInv || loadingV2 || loadingPrix}
              startIcon={(loading || loadingInv || loadingV2 || loadingPrix) ? <CircularProgress size={16} color="inherit"/> : <SearchIcon/>}
              sx={{ height: 40 }}>
              {(loading || loadingInv || loadingV2 || loadingPrix) ? 'Recherche…' : 'Analyser'}
            </Button>
          </Grid>
          {activeTab === 0 && (
            <Grid item xs={12} sm="auto">
              <Button size="small" variant="text" color="inherit"
                endIcon={showFilters ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                onClick={() => setShowFilters(v=>!v)} sx={{ color: '#666' }}>
                {showFilters ? 'Masquer filtres' : 'Filtres avancés'}
              </Button>
            </Grid>
          )}
          {activeTab === 1 && (
            <Grid item xs={6} sm={2} md={1.5}>
              <TextField fullWidth label="Surface min (m²)" value={surfaceMinInv}
                onChange={e => setSurfaceMinInv(e.target.value)}
                size="small" type="number" inputProps={{ min: 0 }}
                placeholder="80" />
            </Grid>
          )}
          {activeTab === 2 && (
            <>
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Déciles</InputLabel>
                  <Select value={decileMax} label="Déciles" onChange={e => setDecileMax(e.target.value)}>
                    <MenuItem value={1}>Décile 1 (10 % les mieux classées)</MenuItem>
                    <MenuItem value={2}>Déciles 1 à 2</MenuItem>
                    <MenuItem value={3}>Déciles 1 à 3</MenuItem>
                    <MenuItem value={5}>Déciles 1 à 5</MenuItem>
                    <MenuItem value={10}>Toutes les silencieuses</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm="auto">
                <FormControlLabel control={<Switch checked={onlySilent} onChange={e => setOnlySilent(e.target.checked)} size="small" />}
                  label={<span style={{ fontSize: '0.85rem' }}>Silencieuses seulement</span>} />
              </Grid>
            </>
          )}
          {activeTab === 3 && (
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={typePrix} label="Type" onChange={e => setTypePrix(e.target.value)}>
                  <MenuItem value="Maison">Maisons</MenuItem>
                  <MenuItem value="Appartement">Appartements</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        {/* Filtres avancés — onglet biens scorés uniquement */}
        {activeTab === 0 && (
          <Collapse in={showFilters}>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type de bien</InputLabel>
                  <Select value={typeLocal} label="Type de bien" onChange={e=>setTypeLocal(e.target.value)}>
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="Maison">Maison</MenuItem>
                    <MenuItem value="Appartement">Appartement</MenuItem>
                    <MenuItem value="Local industriel. commercial ou assimilé">Local commercial</MenuItem>
                    <MenuItem value="Dépendance">Dépendance</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priorité</InputLabel>
                  <Select value={priorite} label="Priorité" onChange={e=>setPriorite(e.target.value)}>
                    <MenuItem value="">Toutes</MenuItem>
                    <MenuItem value="URGENT">🔴 URGENT</MenuItem>
                    <MenuItem value="HIGH">🟠 HIGH</MenuItem>
                    <MenuItem value="MEDIUM">🟡 MEDIUM</MenuItem>
                    <MenuItem value="LOW">🟢 LOW</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2} md={1.5}>
                <TextField fullWidth label="Surface min (m²)" value={surfaceMin}
                  onChange={e=>setSurfaceMin(e.target.value)} size="small" type="number" inputProps={{min:0}} />
              </Grid>
              <Grid item xs={6} sm={2} md={1.5}>
                <TextField fullWidth label="Surface max (m²)" value={surfaceMax}
                  onChange={e=>setSurfaceMax(e.target.value)} size="small" type="number" inputProps={{min:0}} />
              </Grid>
              <Grid item xs={6} sm={2} md={1.5}>
                <TextField fullWidth select label="Vendu à partir de" value={anneeMin} onChange={e=>setAnneeMin(e.target.value)} size="small">
                  <MenuItem value="">Toutes</MenuItem>
                  {Array.from({ length: new Date().getFullYear() - 2013 }, (_, i) => 2014 + i).map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={2} md={1.5}>
                <TextField fullWidth select label="Vendu jusqu'à" value={anneeMax} onChange={e=>setAnneeMax(e.target.value)} size="small">
                  <MenuItem value="">Toutes</MenuItem>
                  {Array.from({ length: new Date().getFullYear() - 2013 }, (_, i) => 2014 + i).map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <TextField fullWidth select label="Trier par" value={sortDvf} onChange={e=>setSortDvf(e.target.value)} size="small">
                  <MenuItem value="score">Score P6 (ancien modèle)</MenuItem>
                  <MenuItem value="detention_desc">Détention la plus longue</MenuItem>
                  <MenuItem value="detention_asc">Détention la plus courte</MenuItem>
                  <MenuItem value="date_desc">Vente la plus récente</MenuItem>
                  <MenuItem value="date_asc">Vente la plus ancienne</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  P6 minimum : <b>{Math.round(minP6*100)}%</b> <span title="Score de l'ancien modèle, calculé pour 40 % des ventes seulement (presque aucune de 2016 à 2019). Pour les maisons, préférez l'onglet Maisons silencieuses.">ⓘ</span>
                </Typography>
                <Slider value={minP6} onChange={(_,v)=>setMinP6(v)} min={0} max={0.9} step={0.05} size="small"
                  marks={[{value:0,label:'0%'},{value:0.5,label:'50%'},{value:0.9,label:'90%'}]} />
              </Grid>
            </Grid>
          </Collapse>
        )}
      </Paper>

      {/* Erreur */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={()=>setError('')}>{error}</Alert>}

      {/* ═══ RÉSULTATS ═══ */}
      {(data || dataInv || dataV2 || dataPrix) && (
        <>
          {/* Onglets */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(_,v)=>setActiveTab(v)}>
              <Tab
                label={
                  <Box sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
                    <span>🏠 Biens scorés DVF</span>
                    {data && <Chip label={data.total} size="small" color="primary" sx={{ height:18, fontSize:'0.7rem' }}/>}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
                    <VisibilityOffIcon sx={{ fontSize:'1rem' }}/>
                    <span>Biens invisibles</span>
                    {dataInv && <Chip label={dataInv.total} size="small" color="warning" sx={{ height:18, fontSize:'0.7rem' }}/>}
                    {!dataInv && loadingInv && <CircularProgress size={12}/>}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
                    <span>🤫 Maisons silencieuses (v2)</span>
                    {dataV2 && <Chip label={dataV2.total} size="small" color="error" sx={{ height:18, fontSize:'0.7rem' }}/>}
                    {!dataV2 && loadingV2 && <CircularProgress size={12}/>}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
                    <span>💶 Prix du secteur</span>
                    {dataPrix && <Chip label={dataPrix.communes.length} size="small" color="success" sx={{ height:18, fontSize:'0.7rem' }}/>}
                    {!dataPrix && loadingPrix && <CircularProgress size={12}/>}
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {/* ── ONGLET 0 : Biens scorés ── */}
          {activeTab === 0 && data && (
            <>
              <Box sx={{ display:'flex', gap:1.5, mb:2, flexWrap:'wrap', alignItems:'center' }}>
                <Chip label={`${data.total} biens`} color="primary" variant="outlined" sx={{ fontWeight:700 }}/>
                {data.stats.urgent > 0 && <Chip label={`🔴 ${data.stats.urgent} URGENT`} sx={{ bgcolor:'#ffebee', color:'#c62828', fontWeight:700 }}/>}
                {data.stats.high > 0 && <Chip label={`🟠 ${data.stats.high} HIGH`} sx={{ bgcolor:'#fff3e0', color:'#e65100', fontWeight:700 }}/>}
                <Chip label={`📍 ${data.stats.avec_coords} géolocalisés`} variant="outlined" size="small"/>
                {data.stats.scoring_ok < data.total && (
                  <Chip label={`⚠️ ${data.total - data.stats.scoring_ok} sans score → Recalcul P6`} color="warning" variant="outlined" size="small"/>
                )}
                <Button variant="outlined" size="small" startIcon={<DownloadIcon/>} onClick={handleExportCSV} sx={{ ml:'auto' }}>Export CSV</Button>
              </Box>

              <Paper elevation={2} sx={{ mb: 2, overflow:'hidden', borderRadius:2 }}>
                <ProspectMap biens={data.biens} L={L} onSelectBien={setSelectedBien} selectedId={selectedBien?.id}/>
                <Box sx={{ px:2, py:0.8, bgcolor:'#fafafa', borderTop:'1px solid #eee', display:'flex', gap:2, flexWrap:'wrap' }}>
                  {Object.entries(PRIORITY_CONFIG).filter(([k])=>k!=='NONE').map(([k,v])=>(
                    <Box key={k} sx={{ display:'flex', alignItems:'center', gap:0.5, fontSize:'0.75rem' }}>
                      <Box sx={{ width:10, height:10, borderRadius:'50%', bgcolor:v.color, border:'1.5px solid white', boxShadow:'0 0 2px rgba(0,0,0,0.3)' }}/>
                      <span style={{ color:'#555' }}>{v.label}</span>
                    </Box>
                  ))}
                </Box>
              </Paper>

              <Box sx={{ display:'flex', gap:2, alignItems:'flex-start' }}>
                <Box sx={{ flex:1, minWidth:0 }}>
                  <Paper elevation={2} sx={{ borderRadius:2 }}>
                    <DataGrid
                      rows={data.biens.map(b => ({ ...b, suivi: suiviDvf[String(b.id)] }))} columns={columns}
                      pageSize={25} rowsPerPageOptions={[25,50,100]}
                      autoHeight disableSelectionOnClick
                      onRowClick={(params) => setSelectedBien(params.row)}
                      onRowDoubleClick={(params) => openDirections(params.row)}
                      getRowClassName={(params) => selectedBien && params.row.id === selectedBien.id ? 'selected-row' : ''}
                      initialState={{ sorting: { sortModel: [{ field:'prob_sell_6m', sort:'desc' }] } }}
                      sx={{
                        border:'none',
                        '& .MuiDataGrid-row:hover': { backgroundColor:'#eef2ff', cursor:'pointer' },
                        '& .MuiDataGrid-row.selected-row': { backgroundColor:'#eff6ff', borderLeft:'3px solid #1976d2' },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor:'#f5f5f5', fontWeight:700 },
                        '& .MuiDataGrid-cell': { borderBottom:'1px solid #f0f0f0' },
                      }}
                    />
                  </Paper>
                </Box>
                {selectedBien && (
                  <Paper elevation={3} sx={{ borderRadius:2, overflow:'hidden', width:380, flexShrink:0, position:{ xs:'static', lg:'sticky' }, top:{ lg:16 }, maxHeight:{ lg:'calc(100vh - 180px)' }, overflowY:'auto' }}>
                    <ScoreExplanationPanel bien={selectedBien} secteurStats={data?.stats} onClose={()=>setSelectedBien(null)}
                      source="dvf" suivi={suiviDvf[String(selectedBien.id)]} onSuiviSaved={(sv) => setSuiviDvf(m => ({ ...m, [String(selectedBien.id)]: sv }))}/>
                  </Paper>
                )}
              </Box>
            </>
          )}

          {/* ── ONGLET 1 : Biens invisibles ── */}
          {activeTab === 1 && (
            <>
              {errorInv && <Alert severity="error" sx={{ mb:2 }} onClose={()=>setErrorInv('')}>{errorInv}</Alert>}
              {loadingInv && (
                <Box sx={{ display:'flex', alignItems:'center', gap:2, py:4, justifyContent:'center' }}>
                  <CircularProgress size={28}/><Typography color="text.secondary">Chargement des biens invisibles…</Typography>
                </Box>
              )}
              {dataInv && !loadingInv && (
                <>
                  {/* Encadré explicatif */}
                  <Alert severity="info" sx={{ mb:2 }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      👻 {dataInv.total.toLocaleString('fr-FR')} biens invisibles sur le CP {cp}
                    </Typography>
                    <Typography variant="body2">
                      Ces parcelles existent au cadastre mais <b>n'ont jamais été vendues depuis 2014</b> dans les données DVF.
                      Ce sont des biens "hors marché" — propriétaires potentiellement vendeurs à démarcher en priorité.
                    </Typography>
                  </Alert>

                  {/* Stats */}
                  <Box sx={{ display:'flex', gap:1.5, mb:2, flexWrap:'wrap', alignItems:'center' }}>
                    <Chip
                      label={`${dataInv.stats.total.toLocaleString('fr-FR')} parcelles`}
                      color="warning" variant="outlined" sx={{ fontWeight:700 }}
                    />
                    <Tooltip title="Cliquez pour filtrer uniquement les maisons (surface ≥ 80m²)">
                      <Chip
                        label={`🏠 ~${dataInv.stats.probables_maisons.toLocaleString('fr-FR')} maisons (>80m²)`}
                        clickable
                        onClick={() => { setSurfaceMinInv('80'); setTimeout(() => handleSearchInvisibles(), 0); }}
                        sx={{ bgcolor: surfaceMinInv === '80' ? '#ffe0b2' : '#fff8e1', color:'#e65100', fontWeight:600, border: surfaceMinInv === '80' ? '2px solid #e65100' : 'none' }}
                      />
                    </Tooltip>
                    <Chip
                      label={`🌳 ${dataInv.stats.grandes_parcelles.toLocaleString('fr-FR')} grandes parcelles (>500m²)`}
                      sx={{ bgcolor:'#e8f5e9', color:'#2e7d32', fontWeight:600 }}
                    />
                    {dataInv.stats.surface_moy > 0 && (
                      <Chip label={`Surface moy. : ${dataInv.stats.surface_moy.toLocaleString('fr-FR')} m²`} variant="outlined" size="small"/>
                    )}
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon/>} onClick={handleExportInvCSV} sx={{ ml:'auto' }}>
                      Export CSV
                    </Button>
                  </Box>

                  <Paper elevation={2} sx={{ borderRadius:2 }}>
                    <DataGrid
                      rows={dataInv.biens}
                      columns={columnsInv}
                      pageSize={50}
                      rowsPerPageOptions={[50, 100, 200]}
                      autoHeight
                      disableSelectionOnClick
                      initialState={{ sorting: { sortModel: [{ field:'surface_m2', sort:'desc' }] } }}
                      sx={{
                        border:'none',
                        '& .MuiDataGrid-row:hover': { backgroundColor:'#fff8e1', cursor:'pointer' },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor:'#fff3e0', fontWeight:700 },
                        '& .MuiDataGrid-cell': { borderBottom:'1px solid #f9f9f9' },
                      }}
                    />
                  </Paper>

                  {dataInv.total > 1000 && (
                    <Alert severity="warning" sx={{ mt:2 }}>
                      Affichage limité aux 1 000 premiers résultats. Utilisez "Surface min" pour affiner, ou exportez en CSV pour tout voir.
                    </Alert>
                  )}
                </>
              )}
            </>
          )}

          {/* ── ONGLET 3 : Prix du secteur ── */}
          {activeTab === 3 && (
            <>
              {errorPrix && <Alert severity="error" sx={{ mb:2 }} onClose={()=>setErrorPrix('')}>{errorPrix}</Alert>}
              {loadingPrix && (
                <Box sx={{ display:'flex', alignItems:'center', gap:2, py:4, justifyContent:'center' }}>
                  <CircularProgress size={28}/><Typography color="text.secondary">Calcul des prix…</Typography>
                </Box>
              )}
              {dataPrix && !loadingPrix && (
                <>
                  <Alert severity="success" sx={{ mb:2 }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>💶 Prix de vente réels ({dataPrix.type_local === 'Maison' ? 'maisons' : 'appartements'}) sur le CP {cp}, actes notariés DVF jusqu'au {dataPrix.derniere_vente_connue}</Typography>
                    <Typography variant="body2">Prix au m² médian sur les 12 derniers mois, fourchette du quart le moins cher au quart le plus cher, évolution par rapport aux 12 mois précédents. Pour estimer une maison précise, cliquez sur une ligne de l'onglet « Maisons silencieuses ».</Typography>
                  </Alert>
                  <Box sx={{ display:'flex', gap:1.5, mb:2, flexWrap:'wrap', alignItems:'center' }}>
                    {dataPrix.tranches.map(t => (
                      <Chip key={t.tranche} label={`${t.tranche} : ${t.prix_m2 ? t.prix_m2.toLocaleString('fr-FR') + ' €/m²' : '—'} (${t.ventes_24m} ventes / 24 mois)`} variant="outlined" sx={{ fontWeight:600 }}/>
                    ))}
                  </Box>
                  <Paper elevation={2} sx={{ borderRadius:2, mb:2 }}>
                    <DataGrid rows={dataPrix.communes} columns={columnsPrix} getRowId={(r) => r.code_commune}
                      pageSize={25} rowsPerPageOptions={[25, 50]} autoHeight disableSelectionOnClick
                      initialState={{ sorting: { sortModel: [{ field:'ventes_12m', sort:'desc' }] } }}
                      sx={{ border:'none', '& .MuiDataGrid-columnHeaders': { backgroundColor:'#e8f5e9', fontWeight:700 }, '& .MuiDataGrid-cell': { borderBottom:'1px solid #f5f5f5' } }} />
                  </Paper>
                  <Typography variant="caption" color="text.secondary">
                    €/m² médian par année : {dataPrix.annees.map(a => `${a.annee} : ${a.prix_m2 ? Math.round(a.prix_m2).toLocaleString('fr-FR') : '—'} (${a.ventes})`).join(' · ')}
                  </Typography>
                </>
              )}
            </>
          )}

          {/* ── ONGLET 2 : Maisons silencieuses (v2) ── */}
          {activeTab === 2 && (
            <>
              {errorV2 && <Alert severity="error" sx={{ mb:2 }} onClose={()=>setErrorV2('')}>{errorV2}</Alert>}
              {loadingV2 && (
                <Box sx={{ display:'flex', alignItems:'center', gap:2, py:4, justifyContent:'center' }}>
                  <CircularProgress size={28}/><Typography color="text.secondary">Chargement du classement…</Typography>
                </Box>
              )}
              {dataV2 && !loadingV2 && (
                <>
                  <Alert severity="info" sx={{ mb:2 }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      🤫 {dataV2.stats.cibles.toLocaleString('fr-FR')} maisons silencieuses de propriétaires privés sur le CP {cp}
                      {' '}(sur {dataV2.stats.maisons.toLocaleString('fr-FR')} maisons, dont {dataV2.stats.dpe_recent.toLocaleString('fr-FR')} avec un DPE récent déjà visible dans DPE Pro)
                    </Typography>
                    <Typography variant="body2">
                      Aucun DPE depuis 30 mois, hors sociétés et hors constructions neuves. Classées par la <b>probabilité de vente dans les 12 mois</b>,
                      calculée <b>avant tout signal</b> (bâti, parcelle, historique de vente, quartier) et calibrée sur la dernière année observée.
                      Lecture : « 1 sur 25 » signifie qu'une maison sur 25 de ce profil se vend dans l'année (une sur 70 en moyenne).
                      Sur 2023 à 2025, le décile 1 a fait <b>1,6 à 1,7 fois plus</b> de ventes que la moyenne : c'est une liste de porte-à-porte prioritaire,
                      pas une liste de vendeurs. Le décile DPE (ancien classement) reste indiqué à titre secondaire. Mise à jour : {dataV2.stats.maj || '—'}.
                    </Typography>
                  </Alert>
                  <Box sx={{ display:'flex', gap:1.5, mb:2, flexWrap:'wrap', alignItems:'center' }}>
                    <Chip label={`${dataV2.total.toLocaleString('fr-FR')} affichées`} color="error" variant="outlined" sx={{ fontWeight:700 }}/>
                    <Chip label={`D1 : ${dataV2.stats.decile_1.toLocaleString('fr-FR')}`} sx={{ bgcolor:'#ffebee', color:'#c62828', fontWeight:700 }}/>
                    <Chip label={`D1-3 : ${dataV2.stats.decile_1_3.toLocaleString('fr-FR')}`} sx={{ bgcolor:'#fff8e1', color:'#e65100', fontWeight:700 }}/>
                    {dataV2.stats.signaux_internes > 0 && <Chip label={`Déjà dans Netty : ${dataV2.stats.signaux_rattaches} rattachées / ${dataV2.stats.signaux_internes}`} sx={{ bgcolor:'#eceff1', color:'#37474f', fontWeight:700 }}/>}
                    <Chip label={`📍 ${dataV2.stats.geolocalisees.toLocaleString('fr-FR')} géolocalisées`} variant="outlined" size="small"/>
                    <Tooltip title="Tire au sort des maisons hors déciles 1 à 3 et les ajoute au suivi (groupe témoin) : à prospecter comme les autres, pour mesurer ce que la liste rapporte de plus">
                      <Button variant="outlined" size="small" color="secondary" onClick={handleTemoins} sx={{ ml:'auto' }}>🎲 Témoins</Button>
                    </Tooltip>
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon/>} onClick={handleExportV2CSV}>Export CSV</Button>
                  </Box>
                  {estimBien && (
                    <Paper elevation={3} sx={{ mb:2, p:2, borderRadius:2, borderLeft:'4px solid #c62828' }}>
                      <Box sx={{ display:'flex', gap:2, alignItems:'center', flexWrap:'wrap' }}>
                        <Box sx={{ flex:1, minWidth:220 }}>
                          <Typography variant="subtitle2" fontWeight={800}>💶 Estimer : {estimBien.adresse} ({estimBien.commune})</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Construit en {estimBien.annee_construction || '?'} · {estimBien.emprise ? `${estimBien.emprise} m² au sol` : ''} · parcelle {estimBien.surf_parcelle ? `${estimBien.surf_parcelle} m²` : '?'}
                            {estimBien.derniere_vente ? ` · vendue ${estimBien.derniere_vente} ${estimBien.dernier_prix ? `(${(estimBien.dernier_prix/1000).toFixed(0)}k€)` : ''}` : ' · jamais vendue depuis 2014'}
                          </Typography>
                        </Box>
                        <TextField label="Surface habitable (m²)" size="small" type="number" value={estimSurface} onChange={e => setEstimSurface(e.target.value)} sx={{ width:180 }} />
                        <Button variant="contained" size="small" color="error" onClick={() => handleEstimer(estimBien, estimSurface)} disabled={loadingEstim}>
                          {loadingEstim ? 'Calcul…' : 'Estimer par comparables'}
                        </Button>
                        <Button size="small" onClick={() => { setEstimBien(null); setEstim(null); }}>Fermer</Button>
                        <Box sx={{ mt: 1.5 }}>
                          <SuiviProspection source="v2" cle={String(estimBien.id)} bien={estimBien} value={suiviV2[String(estimBien.id)]}
                            onSaved={(sv) => setSuiviV2(m => ({ ...m, [String(estimBien.id)]: sv }))} />
                        </Box>
                      </Box>
                      {estim && estim.error && <Alert severity="warning" sx={{ mt:1.5 }}>{estim.error}</Alert>}
                      {estim && !estim.error && (
                        <Box sx={{ mt:1.5 }}>
                          <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap', alignItems:'center' }}>
                            <Chip label={`${(estim.estimation_basse/1000).toFixed(0)} k€ à ${(estim.estimation_haute/1000).toFixed(0)} k€`} sx={{ bgcolor:'#ffebee', color:'#c62828', fontWeight:800, fontSize:'0.95rem', height:30 }}/>
                            <Chip label={`médiane ${(estim.estimation_mediane/1000).toFixed(0)} k€`} variant="outlined" sx={{ fontWeight:700 }}/>
                            <Chip label={`${estim.prix_m2_median} €/m² (q1 ${estim.prix_m2_q1}, q3 ${estim.prix_m2_q3})`} variant="outlined" size="small"/>
                            <Chip label={`${estim.nb_comparables} ventes, ${estim.mode}, ${estim.periode_mois} mois`} variant="outlined" size="small"/>
                            {estim.tendance_commune_12m_vs_24m_prec_pct != null && (
                              <Chip label={`tendance commune ${estim.tendance_commune_12m_vs_24m_prec_pct > 0 ? '+' : ''}${estim.tendance_commune_12m_vs_24m_prec_pct} %`} size="small"
                                sx={{ bgcolor: estim.tendance_commune_12m_vs_24m_prec_pct >= 0 ? '#e8f5e9' : '#fff3e0', fontWeight:600 }}/>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt:1 }}>
                            Comparables : {estim.comparables.slice(0, 8).map(c => `${c.adresse || '?'} (${c.date_mutation.slice(0,4)}, ${c.surface} m², ${(c.prix/1000).toFixed(0)}k€${c.distance_km != null ? `, ${c.distance_km} km` : ''})`).join(' · ')}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  )}
                  <Paper elevation={2} sx={{ mb: 2, overflow:'hidden', borderRadius:2 }}>
                    <ProspectMap L={L} onSelectBien={selectV2} selectedId={estimBien?.id}
                      biens={dataV2.biens.map(b => ({ ...b, prob_sell_6m: b.score, score_label: 'Chance de vente (12 mois)', score_chance: formatChance(b.score),
                        contact_priority: b.decile === 1 ? 'URGENT' : b.decile === 2 ? 'HIGH' : b.decile === 3 ? 'MEDIUM' : 'LOW',
                        type_local: 'Maison', surface: b.emprise, date_mutation: b.derniere_vente }))} />
                  </Paper>
                  <Paper elevation={2} sx={{ borderRadius:2 }}>
                    <DataGrid
                      rows={dataV2.biens.map(b => ({ ...b, suivi: suiviV2[String(b.id)] }))} columns={columnsV2}
                      pageSize={50} rowsPerPageOptions={[50, 100, 200]}
                      autoHeight disableSelectionOnClick
                      onRowClick={(params) => selectV2(params.row)}
                      onRowDoubleClick={(params) => openDirections(params.row)}
                      getRowClassName={(params) => estimBien && params.row.id === estimBien.id ? 'selected-row' : ''}
                      initialState={{ sorting: { sortModel: [{ field:'score', sort:'desc' }] } }}
                      sx={{
                        border:'none',
                        '& .MuiDataGrid-row:hover': { backgroundColor:'#fce4ec', cursor:'pointer' },
                        '& .MuiDataGrid-row.selected-row': { backgroundColor:'#fce4ec', borderLeft:'3px solid #c62828' },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor:'#fdecef', fontWeight:700 },
                        '& .MuiDataGrid-cell': { borderBottom:'1px solid #f9f9f9' },
                      }}
                    />
                  </Paper>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* État vide */}
      {!data && !dataInv && !dataV2 && !dataPrix && !loading && !loadingInv && !loadingV2 && !loadingPrix && !error && (
        <Box sx={{ textAlign:'center', mt:8, color:'#aaa' }}>
          <Typography variant="h2" sx={{ mb:2 }}>🔍</Typography>
          <Typography variant="h6" color="text.secondary">Saisissez un code postal pour analyser les biens du secteur</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt:1 }}>
            Onglet "Biens scorés" : probabilité de revente · "Biens invisibles" : jamais vendus depuis 2014 · "Maisons silencieuses" : probabilité de vente sous 12 mois, avant tout signal
          </Typography>
        </Box>
      )}
    </Box>
  );
}
